import Cookie from "js-cookie"
import delay from "lodash.delay"

import { GOOGLE_GTAG_ID, IS_DEVELOPMENT, ANALYTIC_DEBUG_SHOW_LOG } from "utils/env-management"
import { getTildaUtm } from "utils/utm"
import { nonNullableEntry } from "utils/nonNullable"
import { isBrowser } from "utils/infrastructure/ssg-utils"

const w = isBrowser() ? window : undefined

type Gtag = typeof window.gtag
const dataLayer = w?.dataLayer || []
const ga: Gtag.Gtag =
    w?.gtag ||
    function (...args: unknown[]) {
        dataLayer.push(args)
    }

const gtagCtx: { clientId: string | undefined } = {
    clientId: undefined,
}
export function initGtagCtx() {
    return Promise.race([
        new Promise<void>((resolve) => {
            ga("get", GOOGLE_GTAG_ID, "client_id", (value) => {
                if (typeof value === "string") {
                    gtagCtx.clientId = value
                }
                resolve()
            })
        }),
        new Promise<void>((resolve) => delay(resolve, 300)),
    ])
}

export function gaGetClientIdSync() {
    try {
        const fromSurvey = getSurveyClientId()
        if (fromSurvey) return fromSurvey
        const fromCookie = Cookie.get("_ga")
        if (fromCookie) return fromCookie
        const clientId = gtagCtx.clientId
        return clientId ? "GA1.2." + clientId : clientId
    } catch (e) {
        return undefined
    }
}

function getSurveyClientId() {
    const utm = getTildaUtm()
    if (!utm) {
        return null
    }
    const params = new URLSearchParams(utm.replace(/\|\|\|/g, "&"))
    /**
     * @todo
     * rm this checking later
     * needed because some bad links with utm_survey_client_id=dotmap sended by email
     */
    const id = params.get("utm_survey_client_id")
    return id && !id.includes("dotmap") ? id : null
}

function toGA4Event(
    eventCategory: string | undefined,
    eventAction: string,
    eventLabel: string | undefined,
    eventValue: string | undefined,
    fields: Record<string, string>
): [string, Record<string, string>] {
    const entries: [string, string | undefined][] = [
        ["event_category", eventCategory],
        ["event_label", eventLabel],
        ["event_value", eventValue],
    ]
    return [eventAction, { ...fields, ...Object.fromEntries(entries.filter(nonNullableEntry)) }]
}

export function gaSendEvent(
    eventCategory: string | undefined,
    eventAction: string,
    eventLabel: string | undefined,
    eventValue: string | undefined,
    fieldsObject: Record<string, string>
) {
    const [action, params] = toGA4Event(
        eventCategory,
        eventAction,
        eventLabel,
        eventValue,
        fieldsObject
    )
    runAction(
        () => ga("event", action, { ...params, send_to: GOOGLE_GTAG_ID }),
        `ga('event', ${action}, ${JSON.stringify(params, null, 2)})`
    )
}

export function gaSetUserProperty(name: string, computeValue: () => string) {
    runAction(
        () => ga("set", "user_properties", { [name]: computeValue() }),
        `ga('set', '${name}', <${computeValue.toString()}>)`
    )
}

function runAction(action: () => void, logMessage?: string) {
    !IS_DEVELOPMENT && action()
    if (logMessage && ANALYTIC_DEBUG_SHOW_LOG) {
        console.log(`executed: ${logMessage}`)
    }
}
